import React, { useEffect, useState } from "react";
import { apiClient, formatSAR } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Loader2, TrendingUp, DollarSign, ShieldCheck, Layers, Filter, Award, Users, AlertTriangle, ArrowUp, ArrowDown } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";

const GOLD = "#c9a55c";
const DARK = "#0a0a0a";
const PIE_COLORS = ["#c9a55c", "#8a6f2e", "#5a4615", "#3a3a3a", "#7a7a7a", "#a8893e", "#d4b56e", "#6b6b6b"];

function Card({ title, children, action }) {
  return (
    <div className="surface rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function AnalyticsTab() {
  const { lang } = useI18n();
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiClient.get("/admin/analytics", { params: { period } })
      .then(({ data: d }) => { if (!cancelled) setData({ ...d, _period: period }); });
    return () => { cancelled = true; };
  }, [period]);

  const fresh = data && data._period === period;
  if (!fresh) {
    return <div className="p-10 text-[#525252] flex items-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> {lang === "ar" ? "تحميل..." : "Loading..."}</div>;
  }

  const totalRevenue = data.revenue.reduce((s, p) => s + p.value, 0);
  const totalUsers = data.users.reduce((s, p) => s + p.value, 0);
  const totalVehicles = data.vehicles.reduce((s, p) => s + p.value, 0);
  const totalConv = data.conversations.reduce((s, p) => s + p.value, 0);

  return (
    <div data-testid="admin-analytics" className="space-y-6">
      <LiveVisitorsStrip lang={lang} />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-display text-2xl flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#8a6f2e]" /> {lang === "ar" ? "تحليلات المنصة" : "Platform Analytics"}</h2>
        <div className="flex gap-1 pill p-1">
          {["7d", "30d", "90d"].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1 text-xs rounded-full transition-colors ${period === p ? "bg-[#0a0a0a] text-[#f1ecdf]" : "text-[#3a3a3a]"}`}
              data-testid={`analytics-range-${p}`}
            >
              {p === "7d" ? (lang === "ar" ? "7 أيام" : "7 days") : p === "30d" ? (lang === "ar" ? "30 يوم" : "30 days") : (lang === "ar" ? "90 يوم" : "90 days")}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: lang === "ar" ? "مستخدمون جدد" : "New users", value: totalUsers.toLocaleString() },
          { label: lang === "ar" ? "إعلانات جديدة" : "New listings", value: totalVehicles.toLocaleString() },
          { label: lang === "ar" ? "محادثات جديدة" : "New conversations", value: totalConv.toLocaleString() },
          { label: lang === "ar" ? "إيرادات (SAR)" : "Revenue (SAR)", value: formatSAR(totalRevenue) },
        ].map((c) => (
          <div key={c.label} className="surface rounded-xl p-5">
            <div className="text-xs text-[#6b6b6b] uppercase tracking-widest">{c.label}</div>
            <div className="font-display text-3xl mt-2 text-[#0a0a0a]">{c.value}</div>
          </div>
        ))}
      </div>

      {/* Charts grid */}
      <div className="grid lg:grid-cols-2 gap-5">
        {/* iter238 — User growth chart + new growth-rate sidecar packed
            into ONE outer grid cell so no sibling card ever moves and
            the outer 2-col layout is preserved. Existing chart, its
            data, axes and styling are untouched. */}
        <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-4" data-testid="user-growth-row">
          <Card title={lang === "ar" ? "نمو المستخدمين" : "User growth"}>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.users}>
                <CartesianGrid strokeDasharray="3 3" stroke="#0a0a0a14" />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#6b6b6b" }} />
                <YAxis tick={{ fontSize: 10, fill: "#6b6b6b" }} />
                <Tooltip />
                <Line type="monotone" dataKey="value" stroke={GOLD} strokeWidth={2} dot={false} name={lang === "ar" ? "مستخدمون" : "Users"} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
          <UserGrowthRateCard users={data.users} lang={lang} />
        </div>

        <Card title={lang === "ar" ? "الإعلانات الجديدة" : "New listings"}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.vehicles}>
              <CartesianGrid strokeDasharray="3 3" stroke="#0a0a0a14" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#6b6b6b" }} />
              <YAxis tick={{ fontSize: 10, fill: "#6b6b6b" }} />
              <Tooltip />
              <Bar dataKey="value" fill={DARK} radius={[4, 4, 0, 0]} name={lang === "ar" ? "إعلانات" : "Listings"} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title={lang === "ar" ? "الإيرادات (SAR)" : "Revenue (SAR)"}>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.revenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#0a0a0a14" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#6b6b6b" }} />
              <YAxis tick={{ fontSize: 10, fill: "#6b6b6b" }} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#8a6f2e" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card title={lang === "ar" ? "المحادثات اليومية" : "Daily conversations"}>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.conversations}>
              <CartesianGrid strokeDasharray="3 3" stroke="#0a0a0a14" />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#6b6b6b" }} />
              <YAxis tick={{ fontSize: 10, fill: "#6b6b6b" }} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#3a3a3a" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card title={lang === "ar" ? "توزيع الفئات" : "Categories distribution"}>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={data.categories} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {data.categories.map((c, i) => <Cell key={c.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card title={lang === "ar" ? "أعلى المدن" : "Top cities"}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.top_cities} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#0a0a0a14" />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#6b6b6b" }} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "#3a3a3a" }} width={90} />
              <Tooltip />
              <Bar dataKey="value" fill={GOLD} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Comprehensive insights section (KPIs, funnel, listings status, etc.) */}
      <InsightsSection lang={lang} period={period} />

      {/* Share & referral analytics (UTM tracking) */}
      <ShareAnalyticsSection lang={lang} period={period} />

      {/* iter206 — Top viewed listings (independent from share_visits) */}
      <TopViewedSection lang={lang} period={period} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Share-link / UTM analytics — populated by /api/analytics/share-visits
// ─────────────────────────────────────────────────────────────────────
// iter328 — expanded to cover every source `_normalize_source` and
// `_classify_referrer` (analytics_routes.py) may emit. Unknown sources
// coming from the backend are still rendered dynamically — they fall
// back to a neutral label + colour further down.
const SOURCE_LABEL = {
  whatsapp:  { ar: "واتساب",       en: "WhatsApp",     color: "#25D366" },
  x:         { ar: "إكس",          en: "X",            color: "#0a0a0a" },
  twitter:   { ar: "تويتر",        en: "Twitter",      color: "#1DA1F2" },
  telegram:  { ar: "تليجرام",      en: "Telegram",     color: "#0088cc" },
  facebook:  { ar: "فيسبوك",       en: "Facebook",     color: "#1877F2" },
  instagram: { ar: "إنستقرام",     en: "Instagram",    color: "#E4405F" },
  tiktok:    { ar: "تيك توك",      en: "TikTok",       color: "#010101" },
  snapchat:  { ar: "سناب شات",     en: "Snapchat",     color: "#FFFC00" },
  youtube:   { ar: "يوتيوب",       en: "YouTube",      color: "#FF0000" },
  linkedin:  { ar: "لينكدإن",      en: "LinkedIn",     color: "#0A66C2" },
  google:    { ar: "قوقل",         en: "Google",       color: "#4285F4" },
  bing:      { ar: "بينغ",         en: "Bing",         color: "#008373" },
  apple:     { ar: "آبل",          en: "Apple",        color: "#A2AAAD" },
  email:     { ar: "البريد",       en: "Email",        color: "#525252" },
  copy:      { ar: "رابط منسوخ",   en: "Copied link",  color: "#8a6f2e" },
  native:    { ar: "مشاركة الجهاز", en: "Native share", color: "#c9a55c" },
  qr:        { ar: "رمز QR",       en: "QR Code",      color: "#0a4f3a" },
  direct:    { ar: "مباشر",        en: "Direct",       color: "#3a3a3a" },
  referral:  { ar: "إحالة أخرى",   en: "Referral",     color: "#7a5a1c" },
  other:     { ar: "أخرى",         en: "Other",        color: "#a8893e" },
};

// Fallback used when the backend surfaces a raw source we haven't
// styled yet — the dashboard still renders it dynamically.
const SOURCE_FALLBACK = { color: "#8a6f2e" };
function labelFor(src, lang) {
  const meta = SOURCE_LABEL[src];
  if (meta) return lang === "ar" ? meta.ar : meta.en;
  // Title-case the raw token so `utm_source=custom_campaign_abc` reads
  // as "Custom Campaign Abc" in the panel.
  return String(src || "")
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ") || (lang === "ar" ? "غير معروف" : "Unknown");
}
function colorFor(src) {
  return SOURCE_LABEL[src]?.color || SOURCE_FALLBACK.color;
}

function ShareAnalyticsSection({ lang, period }) {
  const [data, setData] = useState(null);
  const days = period === "7d" ? 7 : period === "90d" ? 90 : 30;

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("/analytics/admin/share-visits/summary", { params: { days } })
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ total_visits: 0, by_source: [], trend: [], top_vehicles: [] }); });
    return () => { cancelled = true; };
  }, [days]);

  if (!data) {
    return (
      <div className="surface rounded-xl p-5 mt-6 inline-flex items-center gap-2 text-sm text-[#6b6b6b]">
        <Loader2 className="w-4 h-4 animate-spin" /> {lang === "ar" ? "تحميل تحليلات المشاركة..." : "Loading share analytics..."}
      </div>
    );
  }

  return (
    <div className="mt-6" data-testid="share-analytics">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-5 h-5 text-[#8a6f2e]" />
        <h2 className="font-display text-2xl">{lang === "ar" ? "تحليلات المشاركة والإحالة" : "Share & Referral Analytics"}</h2>
        <span className="text-xs text-[#8a8a8a]">· {lang === "ar" ? `آخر ${days} يوماً` : `Last ${days} days`}</span>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <Card title={lang === "ar" ? "إجمالي زيارات الإحالة" : "Total referral visits"}>
          <div className="font-display text-4xl text-[#0a0a0a]">{data.total_visits.toLocaleString()}</div>
          <div className="text-xs text-[#8a8a8a] mt-1">
            {lang === "ar" ? "زيارات قادمة من روابط مشاركة المستخدمين" : "Visits from user-shared links"}
          </div>
        </Card>
        <Card title={lang === "ar" ? "أعلى مصدر" : "Top channel"}>
          {data.by_source[0] ? (
            <>
              <div className="font-display text-2xl text-[#0a0a0a]">
                {labelFor(data.by_source[0].source, lang)}
              </div>
              <div className="text-xs text-[#8a8a8a] mt-1">
                {data.by_source[0].count} {lang === "ar" ? "زيارة" : "visits"}
              </div>
            </>
          ) : (
            <div className="text-sm text-[#8a8a8a]">{lang === "ar" ? "لا يوجد بعد" : "No data yet"}</div>
          )}
        </Card>
        <Card title={lang === "ar" ? "أعلى إعلان مُشارَك" : "Most-shared listing"}>
          {data.top_vehicles[0]?.vehicle ? (
            <>
              <div className="font-display text-base text-[#0a0a0a]">
                {data.top_vehicles[0].vehicle.year} {data.top_vehicles[0].vehicle.make} {data.top_vehicles[0].vehicle.model}
              </div>
              <div className="text-xs text-[#8a8a8a] mt-1">
                {data.top_vehicles[0].count} {lang === "ar" ? "زيارة" : "visits"}
              </div>
            </>
          ) : (
            <div className="text-sm text-[#8a8a8a]">{lang === "ar" ? "لا يوجد بعد" : "No data yet"}</div>
          )}
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Card title={lang === "ar" ? "الزيارات حسب المصدر" : "Visits by source"}>
          {data.by_source.length === 0 ? (
            <div className="text-sm text-[#8a8a8a] py-6 text-center">{lang === "ar" ? "لم تُسجَّل زيارات مشاركة بعد." : "No share visits recorded yet."}</div>
          ) : (
            <div className="space-y-2">
              {data.by_source.map((row) => {
                const max = Math.max(...data.by_source.map((r) => r.count));
                const pct = max > 0 ? Math.round((row.count / max) * 100) : 0;
                return (
                  <div key={row.source} data-testid={`share-src-${row.source}`}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-semibold text-[#3a3a3a]">{labelFor(row.source, lang)}</span>
                      <span className="text-[#6b6b6b]">{row.count.toLocaleString()} {lang === "ar" ? "زيارة" : "visits"}</span>
                    </div>
                    <div className="h-2 rounded-full bg-[#0a0a0a]/5 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: colorFor(row.source) }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card title={lang === "ar" ? "أعلى 10 إعلانات مُشارَكة" : "Top 10 shared listings"}>
          {data.top_vehicles.length === 0 ? (
            <div className="text-sm text-[#8a8a8a] py-6 text-center">{lang === "ar" ? "لم تُسجَّل بعد." : "Nothing yet."}</div>
          ) : (
            <ul className="space-y-2">
              {data.top_vehicles.map((row) => (
                <li key={row.vehicle_id} className="flex items-center gap-3 text-sm" data-testid={`share-top-veh-${row.vehicle_id}`}>
                  {row.vehicle?.image && (
                    <img src={row.vehicle.image} alt="" className="w-10 h-10 object-cover rounded-md border border-[#0a0a0a]/10 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[#0a0a0a] truncate">
                      {row.vehicle ? `${row.vehicle.year} ${row.vehicle.make} ${row.vehicle.model}` : (lang === "ar" ? "إعلان محذوف" : "Deleted listing")}
                    </div>
                    <div className="text-xs text-[#8a8a8a]">{row.vehicle?.city || "—"}</div>
                  </div>
                  <span className="font-display text-[#8a6f2e] text-lg shrink-0">{row.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {data.trend.length > 0 && (
        <Card title={lang === "ar" ? "اتجاه زيارات المشاركة اليومي" : "Daily share-visits trend"}>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#0a0a0a14" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6b6b6b" }} />
              <YAxis tick={{ fontSize: 10, fill: "#6b6b6b" }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="count" stroke={GOLD} strokeWidth={2} dot={{ r: 3, fill: DARK }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// iter206 — Top viewed listings (page-view counter, ALL sources).
// Independent widget: reads /analytics/admin/vehicle-views/top.
// Kept separate from ShareAnalyticsSection on purpose so shares and
// views never mix.
// ─────────────────────────────────────────────────────────────────────
function TopViewedSection({ lang, period }) {
  const [data, setData] = useState(null);
  const days = period === "7d" ? 7 : period === "90d" ? 90 : 30;
  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("/analytics/admin/vehicle-views/top", { params: { days, limit: 10 } })
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ total_views: 0, top: [] }); });
    return () => { cancelled = true; };
  }, [days]);

  if (!data) {
    return (
      <div className="surface rounded-xl p-5 mt-6 inline-flex items-center gap-2 text-sm text-[#6b6b6b]">
        <Loader2 className="w-4 h-4 animate-spin" /> {lang === "ar" ? "تحميل أعلى الإعلانات مشاهدة..." : "Loading top-viewed listings..."}
      </div>
    );
  }

  return (
    <div className="mt-6" data-testid="top-viewed-analytics">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-5 h-5 text-[#8a6f2e]" />
        <h2 className="font-display text-2xl">{lang === "ar" ? "أعلى 10 إعلانات مشاهدة" : "Top 10 Viewed Listings"}</h2>
        <span className="text-xs text-[#8a8a8a]">
          · {lang === "ar" ? `آخر ${days} يوماً` : `Last ${days} days`}
          {" · "}
          {lang === "ar" ? `إجمالي المشاهدات: ${data.total_views}` : `Total views: ${data.total_views}`}
        </span>
      </div>
      <Card title={lang === "ar" ? "أعلى 10 إعلانات مشاهدة" : "Top 10 viewed"}>
        {data.top.length === 0 ? (
          <div className="text-sm text-[#8a8a8a] p-4">{lang === "ar" ? "لا توجد مشاهدات بعد." : "No views yet."}</div>
        ) : (
          <ul className="divide-y divide-[#0a0a0a]/[0.06]" data-testid="top-viewed-list">
            {data.top.map((row, idx) => (
              <li key={row.vehicle_id} className="flex items-center gap-3 py-2" data-testid={`top-viewed-row-${idx}`}>
                <span className="text-[#8a6f2e] font-bold w-6 text-center">{row.count}</span>
                {row.vehicle?.image && (
                  <img src={row.vehicle.image} alt="" className="w-14 h-10 rounded object-cover" />
                )}
                <div className="flex-1 min-w-0">
                  {row.vehicle ? (
                    <>
                      <div className="text-sm font-semibold truncate">
                        {row.vehicle.year} {row.vehicle.make} {row.vehicle.model}
                      </div>
                      <div className="text-xs text-[#8a8a8a]">{row.vehicle.city || "—"}</div>
                    </>
                  ) : (
                    <div className="text-sm text-[#8a8a8a]">{lang === "ar" ? "إعلان محذوف" : "Deleted listing"}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Live-visitors pulse + 24-hour hourly chart
// ─────────────────────────────────────────────────────────────────────
function LiveVisitorsStrip({ lang }) {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      apiClient
        .get("/analytics/admin/live")
        .then(({ data }) => { if (!cancelled) setStats(data); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 15_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  if (!stats) {
    return (
      <div className="surface rounded-2xl p-5 inline-flex items-center gap-2 text-sm text-[#6b6b6b]">
        <Loader2 className="w-4 h-4 animate-spin" /> {lang === "ar" ? "تحميل العدّاد الحي..." : "Loading live counter..."}
      </div>
    );
  }
  return (
    <div className="grid md:grid-cols-[260px_260px_1fr] gap-4" data-testid="live-visitors-strip">
      {/* Online now */}
      <div
        className="rounded-2xl p-5 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg,#0a0a0a 0%,#1a1f12 100%)", border: "2px solid #c9a55c" }}
        data-testid="live-online-now"
      >
        <div className="flex items-center gap-2" style={{ color: "#d4ae6a" }}>
          <span className="relative inline-flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
          </span>
          <div className="overline" style={{ color: "#d4ae6a" }}>
            {lang === "ar" ? "متصلون الآن" : "Online now"}
          </div>
        </div>
        <div className="font-display text-5xl mt-2" style={{ color: "#ffffff" }} data-testid="live-online-count">
          {stats.online_now.toLocaleString()}
        </div>
        <div className="text-xs mt-1" style={{ color: "rgba(232,225,207,0.7)" }}>
          {lang === "ar" ? "زائر متواجد على كارتال هذه اللحظة" : "visitors on CARTAL right now"}
        </div>
      </div>
      {/* Last 24h */}
      <div className="surface rounded-2xl p-5">
        <div className="overline overline-gold inline-flex items-center gap-1.5">
          <TrendingUp className="w-3.5 h-3.5" />
          {lang === "ar" ? "آخر 24 ساعة" : "Last 24 hours"}
        </div>
        <div className="font-display text-4xl text-[#0a0a0a] mt-1" data-testid="live-24h-count">
          {stats.last_24h.toLocaleString()}
        </div>
        <div className="text-xs text-[#6b6b6b] mt-1">
          {lang === "ar" ? "زائر فريد" : "unique visitors"}
        </div>
      </div>
      {/* Hourly bars */}
      <div className="surface rounded-2xl p-5">
        <div className="overline overline-gold mb-2">
          {lang === "ar" ? "ساعات الذروة" : "Peak hours (last 24 h, UTC)"}
        </div>
        <ResponsiveContainer width="100%" height={90}>
          <BarChart data={stats.hourly}>
            <XAxis
              dataKey="hour"
              tick={{ fontSize: 9, fill: "#6b6b6b" }}
              interval={2}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip />
            <Bar dataKey="count" fill={GOLD} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Deep insights — KPIs, funnel, financial, listings status, etc.
// ─────────────────────────────────────────────────────────────────────
function InsightsSection({ lang, period }) {
  const [data, setData] = useState(null);
  const [stale, setStale] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setStale(true);
    apiClient
      .get("/admin/analytics/insights", { params: { period } })
      .then(({ data: d }) => { if (!cancelled) { setData(d); setStale(false); } })
      .catch(() => { if (!cancelled) { setData(null); setStale(false); } });
    return () => { cancelled = true; };
  }, [period]);

  if (stale || !data) {
    return (
      <div className="surface rounded-xl p-5 mt-6 inline-flex items-center gap-2 text-sm text-[#6b6b6b]">
        <Loader2 className="w-4 h-4 animate-spin" /> {lang === "ar" ? "تحميل الرؤى المتقدمة..." : "Loading deep insights..."}
      </div>
    );
  }

  const t = data.totals;
  const f = data.financial;

  return (
    <div className="mt-6 space-y-5" data-testid="admin-insights">
      <div className="flex items-center gap-2">
        <Layers className="w-5 h-5 text-[#8a6f2e]" />
        <h2 className="font-display text-2xl">{lang === "ar" ? "رؤى متقدمة على كارتال" : "Deep CARTAL insights"}</h2>
        <span className="text-xs text-[#8a8a8a]">· {lang === "ar" ? `آخر ${data.days} يوماً` : `Last ${data.days} days`}</span>
      </div>

      {/* Totals tiles (all-time) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="insights-totals-row">
        <TotalsTile testid="insights-total-users" icon={Users} label={lang === "ar" ? "إجمالي المستخدمين" : "Total users"} value={t.users} />
        <TotalsTile testid="insights-total-vehicles" icon={Layers} label={lang === "ar" ? "إجمالي الإعلانات" : "Total listings"} value={t.vehicles} />
        <TotalsTile testid="insights-total-conversations" icon={TrendingUp} label={lang === "ar" ? "المحادثات" : "Conversations"} value={t.conversations} />
        <TotalsTile testid="insights-pending-deals" icon={Filter} label={lang === "ar" ? "صفقات معلّقة" : "Pending deals"} value={t.deals_pending} />
        <TotalsTile testid="insights-open-disputes" icon={AlertTriangle} label={lang === "ar" ? "نزاعات مفتوحة" : "Open disputes"} value={t.open_disputes} accent={t.open_disputes > 0 ? "warn" : null} />
        <TotalsTile testid="insights-advertisers" icon={Award} label={lang === "ar" ? "معلنون نشطون" : "Active advertisers"} value={t.active_advertisers} />
      </div>

      {/* Financial KPIs */}
      <div className="surface rounded-xl p-5" data-testid="insights-financial">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#8a6f2e]" /> {lang === "ar" ? "المؤشرات المالية" : "Financial KPIs"}</h3>
          <span className="text-xs text-[#8a8a8a]">{lang === "ar" ? `الفترة: آخر ${data.days} يوماً` : `Window: last ${data.days} days`}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiBox testid="kpi-grand-total" label={lang === "ar" ? "إجمالي الإيرادات" : "Grand revenue"} value={`${formatSAR(f.grand_total_sar)} SAR`} sub={`${f.grand_count} ${lang === "ar" ? "معاملة" : "txns"}`} />
          <KpiBox testid="kpi-boost-rev" label={lang === "ar" ? "إيراد الإعلانات المميّزة" : "Boost revenue"} value={`${formatSAR(f.boost_total_sar)} SAR`} sub={`${f.boost_count} ${lang === "ar" ? "حملة" : "campaigns"}`} accent="gold" />
          <KpiBox testid="kpi-escrow-rev" label={lang === "ar" ? "عمولات الضمان (إسكرو)" : "Escrow commissions"} value={`${formatSAR(f.escrow_total_sar)} SAR`} sub={`${f.escrow_count} ${lang === "ar" ? "صفقة" : "deals"}`} accent="dark" />
          <KpiBox testid="kpi-avg-txn" label={lang === "ar" ? "متوسط قيمة المعاملة" : "Avg. transaction"} value={`${formatSAR(f.avg_txn_sar)} SAR`} sub={`${f.pending_payments} ${lang === "ar" ? "معلّقة" : "pending"}`} />
        </div>
      </div>

      {/* Funnel + Listings status */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="surface rounded-xl p-5" data-testid="insights-funnel">
          <h3 className="font-display text-lg mb-4 flex items-center gap-2"><Filter className="w-4 h-4 text-[#8a6f2e]" /> {lang === "ar" ? "قمع التحويل" : "Conversion funnel"}</h3>
          <FunnelChart steps={data.funnel} lang={lang} />
        </div>

        <div className="surface rounded-xl p-5" data-testid="insights-listings-status">
          <h3 className="font-display text-lg mb-4 flex items-center gap-2"><Layers className="w-4 h-4 text-[#8a6f2e]" /> {lang === "ar" ? "حالة الإعلانات" : "Listings status"}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={data.listings_status.map(r => ({ name: lang === "ar" ? r.name_ar : r.name_en, value: r.value }))}
                dataKey="value"
                nameKey="name"
                cx="50%" cy="50%"
                outerRadius={80}
                label
              >
                {data.listings_status.map((s, i) => <Cell key={s.key || s.name || i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Categories + Top brands */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="surface rounded-xl p-5" data-testid="insights-categories">
          <h3 className="font-display text-lg mb-4">{lang === "ar" ? "الفئات الأكثر طلباً" : "Most-listed categories"}</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={data.categories.map(r => ({ name: lang === "ar" ? r.name_ar : r.name_en, value: r.value }))}
              layout="vertical"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#0a0a0a14" />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#6b6b6b" }} allowDecimals={false} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "#3a3a3a" }} width={90} />
              <Tooltip />
              <Bar dataKey="value" fill={GOLD} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="surface rounded-xl p-5" data-testid="insights-top-brands">
          <h3 className="font-display text-lg mb-4 flex items-center gap-2"><Award className="w-4 h-4 text-[#8a6f2e]" /> {lang === "ar" ? "أعلى 10 ماركات" : "Top 10 brands"}</h3>
          {data.top_brands.length === 0 ? (
            <div className="text-sm text-[#8a8a8a] py-6 text-center">{lang === "ar" ? "لا توجد بيانات" : "No data"}</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.top_brands}>
                <CartesianGrid strokeDasharray="3 3" stroke="#0a0a0a14" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#6b6b6b" }} interval={0} angle={-25} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 10, fill: "#6b6b6b" }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill={DARK} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Vehicle types + Avg price + Trust */}
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="surface rounded-xl p-5" data-testid="insights-vehicle-types">
          <h3 className="font-display text-lg mb-4">{lang === "ar" ? "نوع الإعلان" : "Listing type"}</h3>
          {data.vehicle_types.length === 0 ? (
            <div className="text-sm text-[#8a8a8a] py-6 text-center">{lang === "ar" ? "لا توجد بيانات" : "No data"}</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={data.vehicle_types.map(r => ({ name: lang === "ar" ? r.name_ar : r.name_en, value: r.value }))}
                  dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label
                >
                  {data.vehicle_types.map((r, i) => <Cell key={r.key || r.name_en || i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="surface rounded-xl p-5" data-testid="insights-avg-price">
          <h3 className="font-display text-lg mb-4">{lang === "ar" ? "متوسط السعر / فئة" : "Avg. price / category"}</h3>
          {data.avg_prices.length === 0 ? (
            <div className="text-sm text-[#8a8a8a] py-6 text-center">{lang === "ar" ? "لا توجد بيانات" : "No data"}</div>
          ) : (
            <ul className="space-y-2">
              {data.avg_prices.slice(0, 8).map(r => (
                <li key={r.key} className="flex items-center justify-between gap-2 text-sm" data-testid={`avg-price-${r.key}`}>
                  <span className="text-[#3a3a3a]">{lang === "ar" ? r.name_ar : r.name_en}</span>
                  <span className="font-display text-[#8a6f2e]">{formatSAR(r.avg)} SAR</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="surface rounded-xl p-5" data-testid="insights-trust">
          <h3 className="font-display text-lg mb-4 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#8a6f2e]" /> {lang === "ar" ? "توزيع نقاط الثقة" : "Trust score distribution"}</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={data.trust_buckets.map(r => ({ name: lang === "ar" ? r.name_ar : r.name_en, value: r.value }))}
                dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label
              >
                {data.trust_buckets.map((b, i) => {
                  const colors = { high: "#16a34a", mid: "#c9a55c", low: "#e11d48", unscored: "#737373" };
                  return <Cell key={b.key || i} fill={colors[b.key] || PIE_COLORS[i % PIE_COLORS.length]} />;
                })}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top user cities */}
      {data.user_cities.length > 0 && (
        <div className="surface rounded-xl p-5" data-testid="insights-user-cities">
          <h3 className="font-display text-lg mb-4">{lang === "ar" ? "توزيع المستخدمين حسب المدن" : "Users by city"}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.user_cities} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#0a0a0a14" />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#6b6b6b" }} allowDecimals={false} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "#3a3a3a" }} width={90} />
              <Tooltip />
              <Bar dataKey="value" fill="#8a6f2e" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function TotalsTile({ icon: Icon, label, value, accent, testid }) {
  const warn = accent === "warn";
  return (
    <div
      className="surface rounded-xl p-4"
      style={warn ? { borderColor: "#e11d48", borderWidth: 1 } : undefined}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-[#6b6b6b] text-xs uppercase tracking-widest">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span>{label}</span>
      </div>
      <div className={`font-display text-3xl mt-1 ${warn ? "text-[#e11d48]" : "text-[#0a0a0a]"}`}>
        {Number(value || 0).toLocaleString()}
      </div>
    </div>
  );
}

function KpiBox({ label, value, sub, accent, testid }) {
  const bg = accent === "gold"
    ? "linear-gradient(135deg, #c9a55c 0%, #8a6f2e 100%)"
    : accent === "dark"
      ? "linear-gradient(135deg, #0a0a0a 0%, #2a2a2a 100%)"
      : null;
  const textColor = bg ? "#ffffff" : "#0a0a0a";
  const subColor = bg ? "rgba(255,255,255,0.75)" : "#6b6b6b";
  return (
    <div
      className="rounded-xl p-4"
      style={{ background: bg || "#f7f3e8", border: bg ? "none" : "1px solid #0a0a0a14" }}
      data-testid={testid}
    >
      <div className="text-xs uppercase tracking-widest" style={{ color: subColor }}>{label}</div>
      <div className="font-display text-2xl mt-1" style={{ color: textColor }}>{value}</div>
      <div className="text-xs mt-1" style={{ color: subColor }}>{sub}</div>
    </div>
  );
}

function FunnelChart({ steps, lang }) {
  const max = Math.max(...steps.map(s => s.value), 1);
  const first = steps[0]?.value || 0;
  return (
    <div className="space-y-3">
      {steps.map((s, i) => {
        const pct = max ? Math.round((s.value / max) * 100) : 0;
        const dropFromTop = first ? Math.round((s.value / first) * 100) : 0;
        const colors = ["#c9a55c", "#a8893e", "#8a6f2e", "#5a4615", "#0a0a0a"];
        return (
          <div key={s.stage} data-testid={`funnel-${s.stage}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-semibold text-[#3a3a3a]">{lang === "ar" ? s.label_ar : s.label_en}</span>
              <span className="text-[#6b6b6b]">
                {s.value.toLocaleString()}
                {i > 0 && first > 0 && <span className="text-[#8a6f2e]"> · {dropFromTop}%</span>}
              </span>
            </div>
            <div className="h-3 rounded-full bg-[#0a0a0a]/5 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: colors[i] || "#c9a55c" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// iter239 — Growth-rate sidecar (CORRECT root-cause fix).
//
// Root cause of the previous drifting numbers: the sidecar computed each
// day's percentage as its share of the trailing-7-day delta sum, so any
// new signup shifted the denominator and every historical day changed.
//
// Correct definition per Emergent Support prompt:
//   growth% for day D = ((total_by_end_of_D − total_by_end_of_D-1)
//                        / total_by_end_of_D-1) × 100
//
// We derive the cumulative series from two existing sources (no new API):
//   • `users`            — daily new-user deltas from /admin/analytics
//   • `totals.users`     — all-time user count from /admin/analytics/insights
//
// Then cum[i] is computed backwards from `total_now`:
//   cum[last]   = total_now                (users as of now)
//   cum[i-1]    = cum[i] − delta[i]        (walk back day by day)
//
// Because cum[i-1] depends only on deltas AFTER day i-1 (which are
// historical and immutable), every past day's growth% locks in and never
// shifts when today's counter ticks — which was the reported bug.
//
// No manual numbers, no fallbacks, no fabricated data: if the baseline
// is unavailable OR the prior cumulative is 0, the row simply shows no
// arrow / no percentage rather than inventing one.
// ─────────────────────────────────────────────────────────────────────
function UserGrowthRateCard({ users, lang }) {
  const [totalNow, setTotalNow] = React.useState(null);
  const [totalErr, setTotalErr] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    apiClient
      .get("/admin/analytics/insights")
      .then(({ data }) => {
        if (cancelled) return;
        const t = data?.totals?.users;
        if (typeof t === "number" && t >= 0) setTotalNow(t);
        else setTotalErr(true);
      })
      .catch(() => { if (!cancelled) setTotalErr(true); });
    return () => { cancelled = true; };
  }, []);

  const list = Array.isArray(users) ? users : [];
  const ready = totalNow != null && list.length >= 2;

  // Cumulative user total at the END of each day in the window.
  const N = list.length;
  const cum = new Array(N).fill(0);
  if (ready) {
    cum[N - 1] = totalNow;
    for (let i = N - 1; i > 0; i--) {
      cum[i - 1] = cum[i] - Number(list[i]?.value || 0);
    }
  }

  const growthAt = (i) => {
    if (!ready || i <= 0) return null;
    const prev = cum[i - 1];
    if (prev <= 0) return null;                       // no baseline → no %
    const delta = Number(list[i]?.value || 0);
    return Math.round((delta / prev) * 100);
  };

  const todayIdx = N - 1;
  const yesterdayIdx = N - 2;
  const todayPct = growthAt(todayIdx);
  const yesterdayPct = growthAt(yesterdayIdx);

  // Trend arrow strictly compares TODAY vs YESTERDAY growth rates.
  let trend = "equal";
  if (todayPct != null && yesterdayPct != null) {
    if (todayPct > yesterdayPct) trend = "up";
    else if (todayPct < yesterdayPct) trend = "down";
  }
  const trendColor =
    trend === "up" ? "#16a34a" : trend === "down" ? "#dc2626" : "#525252";
  const TrendIcon =
    trend === "up" ? ArrowUp : trend === "down" ? ArrowDown : null;

  const dayLabel = (i) => {
    if (lang === "ar") {
      if (i === 0) return "اليوم";
      if (i === 1) return "أمس";
      if (i === 2) return "قبل أمس";
      return `منذ ${i} أيام`;
    }
    if (i === 0) return "Today";
    if (i === 1) return "Yesterday";
    return `${i} days ago`;
  };

  // Build last-7-day rows (today first). Each row's arrow compares that
  // day's growth% against the previous day's growth% (both derived from
  // the same immutable cumulative series).
  const rows = [];
  const last7Start = Math.max(0, N - 7);
  for (let idx = N - 1; idx >= last7Start; idx--) {
    const p = growthAt(idx);
    const pPrev = growthAt(idx - 1);
    let dir = null;
    if (p != null && pPrev != null) {
      if (p > pPrev) dir = "up";
      else if (p < pPrev) dir = "down";
    }
    rows.push({ i: N - 1 - idx, pct: p, dir });
  }

  // Loading / error states — never fabricate a percentage.
  const noData = totalErr || (!ready && totalNow == null);

  return (
    <div className="surface rounded-xl p-5" data-testid="user-growth-rate">
      <div className="flex flex-col">
        <h3 className="font-display text-lg">
          {lang === "ar" ? "نسبة نمو المستخدمين" : "User growth rate"}
        </h3>
        <div className="text-xs text-[#8a8a8a] mt-1">
          {lang === "ar" ? "مقارنة اليوم مع أمس" : "Today compared with yesterday"}
        </div>

        {noData ? (
          <div className="text-sm text-[#8a8a8a] mt-6" data-testid="user-growth-rate-empty">
            {lang === "ar" ? "لا توجد بيانات كافية" : "Not enough data yet"}
          </div>
        ) : (
          <>
            {/* Big number + arrow */}
            <div
              className="flex items-baseline gap-2 mt-4"
              data-testid="user-growth-rate-today"
            >
              {TrendIcon && (
                <TrendIcon
                  className="w-8 h-8 shrink-0"
                  style={{ color: trendColor }}
                  aria-label={trend}
                />
              )}
              <div
                className="font-display leading-none"
                style={{ color: trendColor, fontSize: "3rem" }}
              >
                {todayPct != null ? `${todayPct}%` : "—"}
              </div>
            </div>

            {/* Trend badge */}
            <div className="mt-3">
              <span
                className="inline-block text-xs font-semibold px-3 py-1 rounded-full"
                style={{
                  backgroundColor:
                    trend === "up" ? "#dcfce7" : trend === "down" ? "#fee2e2" : "#f5f5f5",
                  color: trendColor,
                  border: `1px solid ${trendColor}33`,
                }}
                data-testid="user-growth-rate-badge"
              >
                {trend === "up"
                  ? (lang === "ar" ? "نمو مقارنة بأمس" : "Higher than yesterday")
                  : trend === "down"
                    ? (lang === "ar" ? "أقل من أمس" : "Lower than yesterday")
                    : (lang === "ar" ? "مساوية لأمس" : "Same as yesterday")}
              </span>
            </div>

            {/* Last 7 days list */}
            <div className="mt-5">
              <div className="text-xs text-[#8a8a8a] mb-2">
                {lang === "ar" ? "آخر 7 أيام" : "Last 7 days"}
              </div>
              <ul className="space-y-1.5 text-sm" data-testid="user-growth-rate-7d">
                {rows.map((r) => {
                  const dirColor =
                    r.dir === "up" ? "#16a34a" : r.dir === "down" ? "#dc2626" : "#8a8a8a";
                  const DirIcon =
                    r.dir === "up" ? ArrowUp : r.dir === "down" ? ArrowDown : null;
                  return (
                    <li
                      key={r.i}
                      className="flex items-center justify-between"
                      data-testid={`user-growth-rate-row-${r.i}`}
                    >
                      <span className="text-[#3a3a3a]">{dayLabel(r.i)}</span>
                      <span
                        className="inline-flex items-center gap-1 font-semibold"
                        style={{ color: dirColor }}
                      >
                        {DirIcon && <DirIcon className="w-3 h-3" />}
                        {r.pct != null ? `${r.pct}%` : "—"}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

